import { ApiBaseResponse } from 'src/app/shared/interfaces/api-base-response.interface';

/** Preview as client opens a share link with a bare `#preview` marker; the token never rides any URL. */
export const PREVIEW_FRAGMENT_KEY = 'preview';

/** The handover with the Projul app embedding a preview: this page posts READY, the app answers with TOKEN. */
export const PREVIEW_READY_MESSAGE = 'projul.preview.ready';
export const PREVIEW_TOKEN_MESSAGE = 'projul.preview.token';

/** Projul names the previewing user's language beside the marker: `#preview&lang=es-ES`. */
export const PREVIEW_LANGUAGE_KEY = 'lang';

/** The header the token travels in to the API; the server reads it nowhere else. */
export const PREVIEW_HEADER = 'X-Projul-Preview';

/** Why a preview cannot show: the server's two refusals, or no token to show it with. */
export type PreviewNotice = 'expired' | 'refused' | 'unavailable';
export type PreviewLanguage = 'en' | 'es';

const PREVIEW_NOTICES: Record<PreviewLanguage, Record<PreviewNotice, string>> = {
  en: {
    expired: 'This preview has expired. Close it and open Preview as client again.',
    refused: 'This preview could not be verified. Close it and open Preview as client again.',
    unavailable: 'This preview cannot open here. Update Projul, or reopen the preview from Projul.',
  },
  es: {
    expired: 'Esta vista previa expiró. Ciérrela y abra Vista previa como cliente de nuevo.',
    refused: 'No se pudo verificar esta vista previa. Ciérrela y abra Vista previa como cliente de nuevo.',
    unavailable: 'Esta vista previa no se puede abrir aquí. Actualice Projul o vuelva a abrir la vista previa desde Projul.',
  },
};

const WRITE_FAILED = 'Your response could not be saved. Refresh the page and try again.';

export function isPreviewRequest(fragment: string | null | undefined): boolean {
  return new URLSearchParams(fragment || '').has(PREVIEW_FRAGMENT_KEY);
}

/** An outdated Projul build opens Preview as client with `?UserId=`; it carries no token, so it can only be refused. */
export function isLegacyPreviewLink(queryParams: Record<string, unknown>): boolean {
  return Object.keys(queryParams).some(key => key.toLowerCase() === 'userid' && String(queryParams[key] ?? '').trim() !== '');
}

export function queryParamsOf(search: string): Record<string, string> {
  const params: Record<string, string> = {};
  new URLSearchParams(search || '').forEach((value, key) => params[key] = value);
  return params;
}

/** A preview is a signed-in user's own look at an unsent document, so its session is never recorded. */
export function isPreviewPage(locationHash: string, locationSearch: string, heldToken: string): boolean {
  return !!heldToken || isPreviewRequest((locationHash || '').replace(/^#/, '')) || isLegacyPreviewLink(queryParamsOf(locationSearch));
}

/** The previewing user's language: the one Projul names in the fragment, else the browser's. */
export function previewLanguage(fragment: string | null | undefined, browserLanguage: string): PreviewLanguage {
  const named = new URLSearchParams((fragment || '').replace(/^#/, '')).get(PREVIEW_LANGUAGE_KEY) || browserLanguage || '';
  return named.toLowerCase().startsWith('es') ? 'es' : 'en';
}

export function previewNoticeText(notice: PreviewNotice, language: PreviewLanguage): string {
  return PREVIEW_NOTICES[language][notice];
}

/** The token a handover message carries, or '' for any other message. */
export function handedOverToken(data: unknown): string {
  const message = data as { type?: unknown; token?: unknown } | null;
  return message?.type === PREVIEW_TOKEN_MESSAGE && typeof message.token === 'string' ? message.token : '';
}

/** Drops the preview header from a recorded request, whatever case the recorder gave its name. */
export function withoutPreviewHeader<T extends { headers?: { [name: string]: unknown } }>(request: T): T {
  const headers = request?.headers;
  if (!headers) return request;
  for (const name of Object.keys(headers)) {
    if (name.toLowerCase() === PREVIEW_HEADER.toLowerCase()) delete headers[name];
  }
  return request;
}

/** The headers a request carries for a preview: the token, and only on calls to the Projul API. */
export function previewHeaders(url: string, apiUrl: string, previewToken: string): Record<string, string> {
  return previewToken && url.startsWith(apiUrl) ? { [PREVIEW_HEADER]: previewToken } : {};
}

export function portalUrlDetailsQuery(id: string, sendNotification: boolean): string {
  return `?id=${encodeURIComponent(id)}&sendNotification=${sendNotification}`;
}

/** A refused answer as the page shows it: a preview the server no longer accepts, and why, or a plain failure. */
export function shareLinkWriteRefusal(error: unknown, previewToken: string | undefined, language: PreviewLanguage): ApiBaseResponse {
  const failure = String(error);
  const previewRefused = !!previewToken && failure.includes('Code: 403');
  const message = previewRefused ? previewNoticeText(failure.includes('preview_expired') ? 'expired' : 'refused', language) : WRITE_FAILED;
  return { IsSuccessful: false, StatusCode: 0, Response: message };
}
